import React, { useEffect, useRef, useState } from 'react';

import type { ObserverOptions } from '../../types.js';
import type { GalleryComponent } from '../../../../../../core/data-sources/content-components/types.js';

import Image from '../Image.js';
import LoopingVideoAsset from '../LoopingVideoAsset/LoopingVideoAsset.js';
import { determineAssetTypeAndContent } from './helpers.js';
import FlyoutText from './FlyoutText.js';

interface FadeGalleryProps extends GalleryComponent {
  isMobile?: boolean;
  layout?: string;
}

const FadeGallery: React.FC<FadeGalleryProps> = ({
  components,
  isMobile,
  layout,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const textElements = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions: ObserverOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const { target, isIntersecting } = entry;

        if (isIntersecting) {
          // Find the index of the intersecting text element
          const index = textElements.current.indexOf(target as HTMLDivElement);
          setCurrentImageIndex(index);
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions,
    );

    // Observe each text element
    textElements.current.forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="fade-gallery">
      <div className="sticky-container">
        <div className="backgrounds">
          {components.map((el, i) => {
            const isCurrentImage = i === currentImageIndex;
            const { width = 0, height = 0 } =
              (el?.imageAsset &&
                'size' in el.imageAsset &&
                el.imageAsset.size) ||
              {};
            const isLandscape = width > height;

            const backgroundClasses = [
              'background',
              isCurrentImage ? 'appear' : '',
              isLandscape ? '' : 'vertical',
            ].join(' ');

            return (
              <div className={backgroundClasses} key={`background-${i}`}>
                {el.type === 'video' ? (
                  <LoopingVideoAsset
                    layout={layout}
                    {...el}
                    visible={isCurrentImage}
                  />
                ) : (
                  <Image
                    imageAsset={el.imageAsset}
                    isMobile={isMobile}
                    fullWidth={isLandscape}
                    lazyLoad
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="foregrounds">
        {components.map((element, i) => {
          const textComponentAsset = determineAssetTypeAndContent(element);

          const foregroundClasses = [
            'foreground',
            element.type === 'video' ? 'video' : '',
          ].join(' ');

          return (
            <div key={`foreground-${i}`}>
              <div
                className={foregroundClasses}
                ref={(el) => {
                  textElements.current[i] = el;
                }}
              >
                {textComponentAsset && <FlyoutText {...textComponentAsset} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FadeGallery;
